/**
 * validation/index — ScheduleDraft 编辑命令校验结果归一化工具层。
 *
 * 本模块把客户端规则、服务端冲突和未来约束引擎返回值收敛为统一的
 * ValidationIssue 结构。编辑管道依赖这里判断阻断错误与可确认 warning，
 * mutation 层不需要理解每个规则来源的原始形状。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → lib/editCommand.js (ValidationIssue 类型), hooks/useScheduleDraftEditPipeline.js (校验阶段)
 */

import {
    VALIDATION_SEVERITY_ERROR,
    VALIDATION_SEVERITY_WARNING,
} from "../editCommand";

/**
 * Creates a normalized validation issue.
 *
 * @param {Object} issue Validation issue fields.
 * @param {string} issue.scope Issue scope, such as "draft", "flight", "field", or "source".
 * @param {string|number=} issue.entityId Entity id within the scope.
 * @param {string=} issue.fieldKey Field key when the issue is field-specific.
 * @param {string=} issue.severity Issue severity. Defaults to "error".
 * @param {string=} issue.code Stable machine-readable issue code.
 * @param {string=} issue.message Human-readable issue description.
 * @param {boolean=} issue.blocking Whether this issue must stop the commit.
 * @param {string=} issue.source Issue producer. Defaults to "client".
 * @returns {import("../editCommand").ValidationIssue} Normalized validation issue.
 */
export function createValidationIssue({
    scope,
    entityId,
    fieldKey,
    severity = VALIDATION_SEVERITY_ERROR,
    code = "VALIDATION_ISSUE",
    message = "Validation issue",
    blocking,
    source = "client",
}) {
    return {
        scope,
        entityId,
        fieldKey,
        severity,
        code,
        message,
        blocking: blocking ?? severity === VALIDATION_SEVERITY_ERROR,
        source,
    };
}

/**
 * Normalizes unknown issue arrays into ValidationIssue objects.
 *
 * @param {*[]|undefined|null} issues Raw issue values from validation rules or server responses.
 * @param {Object=} defaults Default fields applied to each issue.
 * @returns {import("../editCommand").ValidationIssue[]} Normalized issues.
 */
export function normalizeValidationIssues(issues, defaults = {}) {
    if (!Array.isArray(issues)) {
        return [];
    }

    return issues
        .filter(Boolean)
        .map((issue) => createValidationIssue({
            ...defaults,
            ...issue,
        }));
}

/**
 * Returns whether any issue blocks command commit.
 *
 * @param {import("../editCommand").ValidationIssue[]} issues Validation issues.
 * @returns {boolean} True when at least one issue is blocking.
 */
export function hasBlockingIssues(issues) {
    return issues.some((issue) => issue.blocking);
}

/**
 * Splits issues into blocking issues and confirmable warnings.
 *
 * @param {import("../editCommand").ValidationIssue[]} issues Validation issues.
 * @returns {{ blockingIssues: import("../editCommand").ValidationIssue[], warningIssues: import("../editCommand").ValidationIssue[] }} Split issue groups.
 */
export function splitValidationIssues(issues) {
    return {
        blockingIssues: issues.filter((issue) => issue.blocking),
        warningIssues: issues.filter((issue) => !issue.blocking && issue.severity === VALIDATION_SEVERITY_WARNING),
    };
}
